// THE SIDEBAR'S NUMBERS — the pipeline rollup read ORG-WIDE (open seasons,
// no search, no type, no named run), shared across the shell: Ventes' and
// Paiements' badges are each page's whole list, Renouvellements' is the
// sum of every live campaign's to-call count — one call per customer PER
// SEASON, so the badge adds up the lists it stands for
// (lib/salesReasons.pageBadge, meta.campaigns).
//
// Module-level on purpose, like useSeasonCatalog: one reading for the whole
// console, held across pages. The sidebar owns the live subscription (it lives
// as long as the shell); the desk FEEDS it from its own rollup when it is
// unlensed — the same endpoint, no second request — and asks for a fresh
// org-wide read when its own rollup is narrowed. ⚠ A badge is NEVER
// lens-narrowed: « 12 » beside Ventes means twelve in the shop, not twelve of
// this type.
import { shallowRef } from 'vue'
import { jsonapiClient } from '@/api/client'
import { useLiveRefetch } from '@/composables/useLiveRefetch'
import { onApiWrite } from '@/lib/apiWrites'
import type { SalesFlowMeta } from '@/lib/salesFlow'

const meta = shallowRef<SalesFlowMeta | null>(null)
let forOrg: string | null = null
let seq = 0

const refetch = async () => {
  const mine = ++seq
  try {
    const response = await jsonapiClient.get('/api/planning_pipeline', { params: { filter: { season_scope: 'open' } } })
    if (mine === seq) meta.value = response.data.meta
  } catch {
    // A badge is a READING, never a gate: the nav keeps working without it.
  }
}

// ★ THE TAB'S OWN WRITES. The org channel never echoes them back (lib/socketId),
// and the tab that wrote is the one that knows the moment — so the badge
// re-reads on every successful write to a resource it counts, from WHICHEVER
// page made it (the job page's « Marquer signée », a visit finished from the
// board, a run closed…), announced once by the client. Only while a shell
// holds the badge: before `ensure`, nothing is listening for anyone.
// ⚠ COALESCED: a board drag or a burst of writes is ONE re-read, 500 ms after
// the last of them — never one rollup per write.
const COUNTED = new Set([ 'jobs', 'visits', 'proposals', 'invoices', 'work_period_runs' ])
let pending: ReturnType<typeof setTimeout> | null = null
onApiWrite(resource => {
  if (!forOrg || !COUNTED.has(resource)) return
  if (pending) clearTimeout(pending)
  pending = setTimeout(() => { pending = null; void refetch() }, 500)
})

export function usePipelineBadges() {
  // The org switched (or the first read): forget the other shop's numbers.
  const ensure = (orgId: string) => {
    if (forOrg === orgId) return
    forOrg = orgId
    meta.value = null
    void refetch()
  }

  // The desk's own rollup, when it carries no lens — the badge reads it for free.
  // Bumps the sequence: a slower org-wide read already in flight must not
  // overwrite the desk's fresher answer.
  const absorb = (fresh: SalesFlowMeta) => { seq++; meta.value = fresh }

  // One live subscription, from the component that outlives the pages.
  const watchLive = () => useLiveRefetch([ 'Job', 'Proposal', 'Invoice', 'WorkPeriodRun' ], refetch)

  return { meta, refetch, ensure, absorb, watchLive }
}
